/**
 * Central clinic configuration — the source of truth for MedicalPeptides.ai's
 * five VidaVital clinic locations.
 *
 *   NAP  = Name / Address / Phone (kept identical to vidavitalmedical.com)
 *   Slug = /locations/<slug> (stable — do not rename without a redirect)
 *
 * Every consumer of clinic data (directory page, detail page, homepage
 * section, sitemap generator, structured data, prerender snapshot, tests)
 * MUST import from here so NAP corrections propagate site-wide from one
 * edit. Anything that duplicates NAP inline is a regression.
 *
 * Data verified against vidavitalmedical.com/locations/ on 2026-08-13 (original)
 * and re-verified 2026-02-17 for parking/directions source-alignment + the
 * Coral-Gables-only "IV" service badge. No coordinates, no invented sameAs
 * URLs, no efficacy claims.
 */

export const CLINIC_REVIEWERS = [
  {
    name: "Christopher Patino",
    credentials: "APRN, Adult-Gerontology",
    profile: "/providers/christopher-patino",
    role: "Nurse Practitioner",
  },
  {
    name: "Dr. Yessenia Quirola",
    credentials: "DNP, APRN, FNP-BC, NP-C",
    profile: "/providers/yessenia-quirola",
    role: "Nurse Practitioner (Doctor of Nursing Practice)",
    // The "Dr." prefix reflects the DNP degree. It is not an MD/DO title.
    // Rendered verbatim next to the credentials so patients see the full context.
    dnp_disclosure: "The \u201CDr.\u201D title reflects Dr. Quirola's Doctor of Nursing Practice (DNP) degree \u2014 not an MD or DO.",
  },
];

export const CLINICS = [
  {
    slug: "las-vegas-nv",
    name: "Las Vegas",
    display_name: "VidaVital Medical \u2014 Las Vegas",
    city: "Las Vegas",
    state: "NV",
    state_full: "Nevada",
    zip: "89118",
    street: "6590 S Rainbow Blvd, Ste 250",
    address_line_1: "6590 S Rainbow Blvd",
    address_line_2: "Ste 250",
    address_full: "6590 S Rainbow Blvd, Ste 250, Las Vegas, NV 89118",
    phone_display: "(702) 602-8629",
    phone_e164: "+17026028629",
    email: "info@vidavitalmedical.com",
    hours: "Mon\u2013Fri by appointment; Sat/Sun closed.",
    hours_schema: [
      { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "09:00", closes: "17:00", description: "By appointment only" },
    ],
    modality: "In-clinic evaluations plus Nevada-licensed telehealth",
    services_summary: "In-clinic + Nevada telehealth (where clinically appropriate)",
    telehealth_state: "Nevada",
    metro: "Las Vegas Valley",
    nearby_communities: ["Spring Valley", "Summerlin", "Henderson", "Paradise", "Enterprise", "Las Vegas Valley"],
    // Parking / directions copy is source-aligned to vidavitalmedical.com/locations/.
    // Building-floor, garage, and unverified neighborhood details were removed
    // because they were inferred rather than sourced from the public VidaVital
    // pages.
    parking_note: "Free parking on-site. Located on South Rainbow Boulevard, minutes from I-215 and US-95, serving the Las Vegas Valley.",
    intro_line: "Provider-led peptide-therapy consultations for the Las Vegas Valley \u2014 in-clinic on South Rainbow Boulevard and telehealth for patients located in Nevada, with a Nevada-licensed provider, where clinically appropriate.",
    local_faq: [
      {
        q: "Where is the Las Vegas VidaVital Medical clinic located?",
        a: "The Las Vegas clinic is at 6590 S Rainbow Blvd, Ste 250, Las Vegas, NV 89118. Free parking is available on-site. The clinic is minutes from I-215 and US-95 and serves the Las Vegas Valley.",
      },
      {
        q: "Do you offer telehealth for patients outside Las Vegas?",
        a: "Yes, our providers offer secure-video telehealth to patients in Nevada where clinically appropriate. Interstate telehealth eligibility depends on state licensure and applicable telemedicine laws; your provider will confirm eligibility during intake.",
      },
      {
        q: "How do I book my first appointment in Las Vegas?",
        a: "Submit the consultation-request form and a member of our team will follow up to schedule your visit at the Las Vegas clinic or by telehealth if appropriate. Every patient starts with a licensed medical provider evaluation.",
      },
      {
        q: "Do you accept walk-ins at the Las Vegas clinic?",
        a: "No. Visits are by appointment only, Monday through Friday. This lets us give every patient the time needed for a proper evaluation and follow-up plan.",
      },
    ],
  },
  {
    slug: "albuquerque-nm",
    name: "Albuquerque",
    display_name: "VidaVital Medical \u2014 Albuquerque",
    city: "Albuquerque",
    state: "NM",
    state_full: "New Mexico",
    zip: "87109",
    street: "7920 Wyoming Blvd NE, Ste B-2",
    address_line_1: "7920 Wyoming Blvd NE",
    address_line_2: "Ste B-2",
    address_full: "7920 Wyoming Blvd NE, Ste B-2, Albuquerque, NM 87109",
    phone_display: "(505) 537-8484",
    phone_e164: "+15055378484",
    email: "info@vidavitalmedical.com",
    hours: "Mon\u2013Fri by appointment; Sat/Sun closed.",
    hours_schema: [
      { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "09:00", closes: "17:00", description: "By appointment only" },
    ],
    modality: "In-clinic evaluations plus New Mexico-licensed telehealth",
    services_summary: "In-clinic + New Mexico telehealth (where clinically appropriate)",
    telehealth_state: "New Mexico",
    metro: "Albuquerque metro",
    nearby_communities: ["Northeast Heights", "Far Northeast Heights", "Westside", "Nob Hill", "Downtown", "Old Town", "Rio Rancho", "Santa Fe"],
    parking_note: "Free parking on-site. Located on Wyoming Boulevard NE, with access from I-40 and the Pan American Freeway, serving the Albuquerque metro.",
    intro_line: "Provider-led peptide-therapy consultations for the Albuquerque metro \u2014 in-clinic in Northeast Heights and telehealth for patients located in New Mexico, with a New-Mexico-licensed provider, where clinically appropriate.",
    local_faq: [
      {
        q: "Where is the Albuquerque VidaVital Medical clinic?",
        a: "The Albuquerque clinic is at 7920 Wyoming Blvd NE, Ste B-2, Albuquerque, NM 87109, in Northeast Heights.",
      },
      {
        q: "Do you serve patients outside Albuquerque city limits?",
        a: "Yes. In-clinic visits welcome patients from Rio Rancho, Santa Fe, the Westside, Nob Hill, Old Town, Downtown, and the wider Albuquerque metro. Telehealth is available for patients located in New Mexico, with a New-Mexico-licensed provider, where clinically appropriate.",
      },
      {
        q: "How does the first consultation work?",
        a: "You submit the online consultation request. A member of our team follows up to schedule an intake with a licensed provider, who reviews your history and goals and determines whether peptide therapy is clinically appropriate.",
      },
      {
        q: "Do you take insurance in Albuquerque?",
        a: "No. VidaVital Medical is a self-pay practice; we do not accept insurance for these services. Your provider will explain any applicable consultation and follow-up fees during intake.",
      },
    ],
  },
  {
    slug: "coral-gables-fl",
    name: "Coral Gables",
    display_name: "VidaVital Medical \u2014 Coral Gables",
    city: "Coral Gables",
    state: "FL",
    state_full: "Florida",
    zip: "33134",
    street: "2030 Douglas Rd, Ste 201",
    address_line_1: "2030 Douglas Rd",
    address_line_2: "Ste 201",
    address_full: "2030 Douglas Rd, Ste 201, Coral Gables, FL 33134",
    phone_display: "(305) 564-6926",
    phone_e164: "+13055646926",
    email: "info@vidavitalmedical.com",
    hours: "Mon\u2013Fri by appointment; Sat/Sun closed.",
    hours_schema: [
      { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "09:00", closes: "17:00", description: "By appointment only" },
    ],
    modality: "In-clinic evaluations plus Florida-licensed telehealth, with on-site IV therapy administered by licensed RNs",
    // Directory card + JSON-LD-adjacent summary. Only Coral Gables carries
    // the "+ IV" suffix because IV therapy is a Coral-Gables-only service
    // per vidavitalmedical.com/locations/.
    services_summary: "In-clinic + Florida telehealth + IV",
    iv_therapy_available: true,
    telehealth_state: "Florida",
    metro: "Miami-Dade County",
    nearby_communities: ["Coconut Grove", "Miami", "Kendall", "Pinecrest", "South Miami", "Miami-Dade"],
    parking_note: "Free parking on-site. Located off Douglas Road, minutes from US-1 and the Dolphin Expressway, serving Miami-Dade.",
    intro_line: "Provider-led peptide-therapy consultations for Miami-Dade \u2014 in-clinic in the Coral Gables business district and telehealth for patients located in Florida, with a Florida-licensed provider, where clinically appropriate.",
    local_faq: [
      {
        q: "Is the Coral Gables clinic accessible from Miami and Coconut Grove?",
        a: "Yes. The clinic is on Douglas Road in the Coral Gables business district, minutes from Coconut Grove, South Miami, Kendall, Pinecrest, and downtown Miami.",
      },
      {
        q: "Do you offer Florida telehealth from the Coral Gables clinic?",
        a: "Yes. Our providers offer secure-video telehealth for patients located in Florida, with a Florida-licensed provider, where clinically appropriate. Your provider will confirm eligibility during intake.",
      },
      {
        q: "What happens at the first Coral Gables visit?",
        a: "Your visit begins with a licensed provider reviewing your goals, medical history, and any relevant labs. If peptide therapy is clinically appropriate, your provider outlines a personalized plan and follow-up cadence.",
      },
      {
        q: "Is parking available at the Coral Gables clinic?",
        a: "Yes. Free parking is available on-site at 2030 Douglas Road.",
      },
    ],
  },
  {
    slug: "brookfield-wi",
    name: "Brookfield",
    display_name: "VidaVital Medical \u2014 Brookfield",
    city: "Brookfield",
    state: "WI",
    state_full: "Wisconsin",
    zip: "53005",
    street: "17100 W North Ave, Ste 200",
    address_line_1: "17100 W North Ave",
    address_line_2: "Ste 200",
    address_full: "17100 W North Ave, Ste 200, Brookfield, WI 53005",
    phone_display: "(414) 404-8012",
    phone_e164: "+14144048012",
    email: "info@vidavitalmedical.com",
    hours: "Mon\u2013Fri by appointment; Sat/Sun closed.",
    hours_schema: [
      { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "09:00", closes: "17:00", description: "By appointment only" },
    ],
    modality: "In-clinic evaluations plus Wisconsin-licensed telehealth",
    services_summary: "In-clinic + Wisconsin telehealth (where clinically appropriate)",
    telehealth_state: "Wisconsin",
    metro: "Southeastern Wisconsin",
    nearby_communities: ["Wauwatosa", "Milwaukee", "Waukesha", "Menomonee Falls", "Pewaukee", "New Berlin", "southeastern Wisconsin"],
    parking_note: "Free parking on-site. Located off West North Avenue, minutes from I-94 and Bluemound Road, serving southeastern Wisconsin.",
    intro_line: "Provider-led peptide-therapy consultations for southeastern Wisconsin \u2014 in-clinic on West North Avenue in Brookfield and telehealth for patients located in Wisconsin, with a Wisconsin-licensed provider, where clinically appropriate.",
    local_faq: [
      {
        q: "Which Wisconsin communities does the Brookfield clinic serve?",
        a: "The clinic sees patients from Brookfield, Wauwatosa, Milwaukee, Waukesha, Menomonee Falls, Pewaukee, New Berlin, and the wider southeastern Wisconsin area. Wisconsin telehealth is available where clinically appropriate.",
      },
      {
        q: "How do I get to 17100 W North Avenue?",
        a: "The clinic is at 17100 W North Ave, Ste 200, Brookfield, WI 53005. Free parking is available on-site. It is minutes from I-94 and Bluemound Road.",
      },
      {
        q: "Do you offer Milwaukee-area telehealth?",
        a: "Yes. Our providers offer telehealth by secure video for patients located in Wisconsin, with a Wisconsin-licensed provider, where clinically appropriate. Interstate telehealth eligibility depends on state licensure and applicable telemedicine laws.",
      },
      {
        q: "How do I schedule a first visit in Brookfield?",
        a: "Submit the online consultation request. A member of our team will follow up to book your intake with a licensed provider at the Brookfield clinic or by Wisconsin telehealth if clinically appropriate.",
      },
    ],
  },
  {
    slug: "omaha-ne",
    name: "Omaha",
    display_name: "VidaVital Medical \u2014 Omaha",
    city: "Omaha",
    state: "NE",
    state_full: "Nebraska",
    zip: "68114",
    street: "444 Regency Pkwy Dr, Ste 204",
    address_line_1: "444 Regency Pkwy Dr",
    address_line_2: "Ste 204",
    address_full: "444 Regency Pkwy Dr, Ste 204, Omaha, NE 68114",
    phone_display: "(402) 267-9519",
    phone_e164: "+14022679519",
    email: "info@vidavitalmedical.com",
    hours: "Mon\u2013Fri by appointment; Sat/Sun closed.",
    hours_schema: [
      { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "09:00", closes: "17:00", description: "By appointment only" },
    ],
    modality: "In-clinic evaluations plus Nebraska-licensed telehealth",
    services_summary: "In-clinic + Nebraska telehealth (where clinically appropriate)",
    telehealth_state: "Nebraska",
    metro: "Omaha metro",
    // Iowa telehealth availability is NOT verified. Council Bluffs is mentioned
    // only as a factual nearby-community reference, not as a service-area claim.
    // Editorial rule (do not surface as visible copy): Council Bluffs is travel
    // context only; do not imply Iowa telehealth anywhere on this page.
    nearby_communities: ["West Omaha", "Midtown", "Downtown", "Bellevue", "Council Bluffs"],
    parking_note: "Free parking on-site. Located off Regency Parkway, minutes from I-680 and Dodge Street, serving the Omaha metro.",
    intro_line: "Provider-led peptide-therapy consultations for the Omaha metro \u2014 in-clinic on Regency Parkway Drive and telehealth for patients located in Nebraska, with a Nebraska-licensed provider, where clinically appropriate.",
    local_faq: [
      {
        q: "Does the Omaha clinic offer Iowa telehealth to Council Bluffs residents?",
        a: "Only Nebraska-licensed telehealth is verified. Council Bluffs residents who wish to see a VidaVital provider are welcome to visit the Omaha clinic in person; interstate telehealth eligibility depends on state licensure and applicable telemedicine laws.",
      },
      {
        q: "Where is the Omaha clinic located?",
        a: "The Omaha clinic is at 444 Regency Pkwy Dr, Ste 204, Omaha, NE 68114. Free parking is available on-site. The clinic is minutes from I-680 and Dodge Street.",
      },
      {
        q: "Which Nebraska communities does the clinic serve?",
        a: "In-clinic visits welcome patients from West Omaha, Midtown, Downtown, Bellevue, and the wider Omaha metro. Nebraska-licensed telehealth is available statewide where clinically appropriate.",
      },
      {
        q: "How do I schedule a first Omaha appointment?",
        a: "Submit the online consultation request. A member of our team will follow up to schedule your intake with a licensed provider at the Omaha clinic or by Nebraska telehealth if clinically appropriate.",
      },
    ],
  },
];

export function findClinicBySlug(slug) {
  if (!slug) return null;
  return CLINICS.find((c) => c.slug === slug) || null;
}

export const CLINIC_SLUGS = CLINICS.map((c) => c.slug);
